import * as React from 'react';
import { Map } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import PropTypes from 'prop-types';

MapViewer.propTypes = {
  coordinates: PropTypes.arrayOf(
    PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
      timestamp: PropTypes.number
    })
  ),
  currentTime: PropTypes.instanceOf(Date),
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
      startTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      endTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.number
    })
  )
};


MapViewer.defaultProps = {
  coordinates: [],
  currentTime: new Date(),
  events: []
};

export default function MapViewer({ coordinates, currentTime, events }) {
    const [map, setMap] = React.useState(null);
    const animationRef = React.useRef(null);
    
    const findCurrentPosition = React.useCallback((currentTime) => {
        if (!coordinates || coordinates.length === 0) return 0;
        
        // Convert currentTime to timestamp for comparison
        const currentTimestamp = currentTime.getTime();
        
        // Find the last position that is before or equal to current time
        let i = coordinates.length - 1;
        while (i >= 0) {
            if (coordinates[i].timestamp <= currentTimestamp) {
                return i;
            }
            i -= 1;
        }
        return 0;
    }, [coordinates]);

    // Find coordinates for a given timestamp
    const findCoordinatesForTimestamp = React.useCallback((timestamp) => {
        if (!coordinates || coordinates.length === 0) return null;
        
        const targetTime = new Date(timestamp).getTime();
        
        // Find the closest coordinate by timestamp
        let closestIndex = 0;
        let minDiff = Math.abs(coordinates[0].timestamp - targetTime);
        
        for (let i = 1; i < coordinates.length; i += 1) {
            const diff = Math.abs(coordinates[i].timestamp - targetTime);
            if (diff < minDiff) {
                minDiff = diff;
                closestIndex = i;
            }
        }
        
        return coordinates[closestIndex];
    }, [coordinates]);

    // Update marker position and covered trail based on current time
    const updateMarkerPosition = React.useCallback(() => {
        if (!map || !coordinates || coordinates.length === 0) return;
        
        const currentIndex = findCurrentPosition(currentTime);
        const currentCoord = coordinates[currentIndex];
        
        if (currentCoord) {
            // Update marker position
            map.getSource('current-location')?.setData({
                type: 'Feature',
                properties: {},
                geometry: {
                    type: 'Point',
                    coordinates: [currentCoord.lng, currentCoord.lat]
                }
            });

            // Update covered trail (green line)
            const coveredCoordinates = coordinates.slice(0, currentIndex + 1).map(coord => [Number(coord.lng), Number(coord.lat)]);
            
            map.getSource('covered-trail')?.setData({
                type: 'Feature',
                properties: {},
                geometry: {
                    type: 'LineString',
                    coordinates: coveredCoordinates
                }
            });
        }
        
        animationRef.current = requestAnimationFrame(updateMarkerPosition);
    }, [map, coordinates, currentTime, findCurrentPosition]);

    // Start/stop animation based on coordinates availability
    React.useEffect(() => {
        if (coordinates && coordinates.length > 0 && map) {
            updateMarkerPosition();
        }
        
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [coordinates, map, updateMarkerPosition]);

    // Update event markers when events change
    React.useEffect(() => {
        if (!map || !events || events.length === 0) return;

        // Create event markers with coordinates
        const eventMarkers = events
            .map(event => {
                const coord = findCoordinatesForTimestamp(event.startTime);
                if (!coord) return null;
                
                return {
                    type: 'Feature',
                    properties: {
                        id: event.id,
                        label: event.label,
                        value: event.value,
                        startTime: event.startTime,
                        endTime: event.endTime
                    },
                    geometry: {
                        type: 'Point',
                        coordinates: [Number(coord.lng), Number(coord.lat)]
                    }
                };
            })
            .filter(marker => marker !== null);

        // Update event markers source
        map.getSource('event-markers')?.setData({
            type: 'FeatureCollection',
            features: eventMarkers
        });
    }, [map, events, findCoordinatesForTimestamp]);

    React.useEffect(() => {
        if (coordinates.length === 0) {
            return undefined;
        }

        const mapInstance = new Map({
            container: 'map',
            style: 'https://api.maptiler.com/maps/streets/style.json?key=ECY4hIN9jl9pfBMhf6BO',
            center: [coordinates[0].lng, coordinates[0].lat],
            zoom: 20,
            reuseMaps: true,
        });

        // convert string coordinates to numbers
        const trail = {
            type: 'LineString',
            coordinates: coordinates.map(coord => [Number(coord.lng), Number(coord.lat)])
        }

        // Create points for all coordinates
        const coordinatePoints = {
            type: 'FeatureCollection',
            features: coordinates.map(coord => ({
                type: 'Feature',
                properties: {},
                geometry: {
                    type: 'Point',
                    coordinates: [Number(coord.lng), Number(coord.lat)]
                }
            }))
        };

        mapInstance.on('load', () => {
            // Add the full trail line (red)
            mapInstance.addSource('trail', {
                type: 'geojson',
                data: {
                    type: 'Feature',
                    properties: {},
                    geometry: trail
                }
            });

            mapInstance.addLayer({
                id: 'trail-line',
                type: 'line',
                source: 'trail',
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round'
                },
                paint: {
                    'line-color': '#ff0000',
                    'line-width': 3
                }
            });

            // Add covered trail line (green) - initially empty
            mapInstance.addSource('covered-trail', {
                type: 'geojson',
                data: {
                    type: 'Feature',
                    properties: {},
                    geometry: {
                        type: 'LineString',
                        coordinates: []
                    }
                }
            });

            mapInstance.addLayer({
                id: 'covered-trail-line',
                type: 'line',
                source: 'covered-trail',
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round'
                },
                paint: {
                    'line-color': '#00ff00',
                    'line-width': 4
                }
            });

            // Add coordinate points (white dots)
            mapInstance.addSource('coordinate-points', {
                type: 'geojson',
                data: coordinatePoints
            });

            mapInstance.addLayer({
                id: 'coordinate-points',
                type: 'circle',
                source: 'coordinate-points',
                paint: {
                    'circle-radius': 3,
                    'circle-color': '#ffffff',
                    'circle-stroke-color': '#000000',
                    'circle-stroke-width': 1
                }
            });

            // Add event markers (orange triangles)
            mapInstance.addSource('event-markers', {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: []
                }
            });

            mapInstance.addLayer({
                id: 'event-markers',
                type: 'circle',
                source: 'event-markers',
                paint: {
                    'circle-radius': 8,
                    'circle-color': '#ff0000',
                    'circle-stroke-color': '#ffffff',
                    'circle-stroke-width': 2
                }
            });

            // Add event marker labels
            mapInstance.addLayer({
                id: 'event-markers-labels',
                type: 'symbol',
                source: 'event-markers',
                layout: {
                    'text-field': ['get', 'label'],
                    'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
                    'text-offset': [0, 1.25],
                    'text-anchor': 'top',
                    'text-size': 12
                },
                paint: {
                    'text-color': '#000000',
                    'text-halo-color': '#ffffff',
                    'text-halo-width': 1
                }
            });

            // Add current location marker - on top of everything
            mapInstance.addSource('current-location', {
                type: 'geojson',
                data: {
                    type: 'Feature',
                    properties: {},
                    geometry: {
                        type: 'Point',
                        coordinates: [coordinates[0].lng, coordinates[0].lat]
                    }
                }
            });

            mapInstance.addLayer({
                id: 'current-location-marker',
                type: 'circle',
                source: 'current-location',
                paint: {
                    'circle-radius': 8,
                    'circle-color': '#007cbf',
                    'circle-stroke-color': '#87ceeb',
                    'circle-stroke-width': 2
                }
            });

        });

        setMap(mapInstance);

        return () => {
            if (mapInstance) {
                mapInstance.remove();
            }
        };
    }, [coordinates]);

    return (
        // Make sure you set the height and width of the map container otherwise the map won't show
        <div 
            id="map"
            style={{ width: '100%', height: 400 }}
        />
    );
}